import { EnumAddressLocationType } from "gql/generated";
import { createGoogleMapsUrl, getCenterAndZoom } from "shared/utils";
import { styled } from "styled-system/jsx";

interface StaticMapProps {
  locations: {
    latitude: number;
    longitude: number;
  }[];
  zoom?: number;
  width?: number;
  height?: number;
  marker?: boolean;
  className?: string;
  style?: React.CSSProperties;
  alwaysShowPinLabel?: boolean;
  disableClick?: boolean;
}

const StyledStaticMap = styled("div", {
  base: {
    userSelect: "none",
    width: "full",
    height: "auto",
    borderRadius: "100",
    border: "1px solid token(colors.border.light)",
    overflow: "hidden",
    bg: "bg.lighter",
    minH: "250px",
  },
});

export const StaticMap = ({
  locations,
  zoom = 14,
  width = 600,
  height = 400,
  marker = true,
  className,
  style,
  alwaysShowPinLabel = false,
  disableClick = false,
}: StaticMapProps) => {
  const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  const mapStyle = import.meta.env.VITE_MAPBOX_STYLESHEET;
  const mapStyleId = mapStyle.startsWith("mapbox://styles/")
    ? mapStyle.replace("mapbox://styles/", "")
    : mapStyle;

  const markers = !marker
    ? ""
    : locations.length === 1 && !alwaysShowPinLabel
      ? `pin-l+8270DB(${locations[0].longitude},${locations[0].latitude})/`
      : locations
          .map(
            (location, index) =>
              `pin-l-${index + 1}+8270DB(${location.longitude},${location.latitude})`,
          )
          .join(",") + "/";

  const centerAndZoom =
    locations.length === 1
      ? `${locations[0].longitude},${locations[0].latitude},${zoom}`
      : (getCenterAndZoom(locations, width, height) as string);

  // Mapbox Static Images API only supports a @2x scale factor for requests;
  // we can't use window.devicePixelRatio directly.
  const scale = window.devicePixelRatio > 1 ? "@2x" : "";

  const staticMapUrl = `https://api.mapbox.com/styles/v1/${mapStyleId}/static/${markers}${centerAndZoom}/${width}x${height}${scale}?access_token=${accessToken}`;

  const handleClick = () => {
    window.open(
      createGoogleMapsUrl({
        location: {
          type: EnumAddressLocationType.Point,
          coordinates: [locations[0].longitude, locations[0].latitude],
        },
      }),
      "_blank",
      "noopener noreferrer",
    );
  };

  return (
    <StyledStaticMap className={className} style={style}>
      <styled.img
        src={staticMapUrl}
        alt="Static map"
        width="100%"
        height="auto"
        onClick={disableClick ? undefined : handleClick}
        cursor={disableClick ? "default" : "pointer"}
        tabIndex={disableClick ? -1 : 0}
      />
    </StyledStaticMap>
  );
};
